import React, { useContext, useEffect, useState } from "react";
import cross_icon from "../Assets/cart_cross_icon.png";
import { ShopContext } from "../../Context/ShopContext";
import { backend_url, currency } from "../../App";
import {
  validateCoupon as apiValidateCoupon,
  placeOrder as apiPlaceOrder,
  getUser as apiGetUser,
  updateProfile as apiUpdateProfile,
  getPaymentSettings,
} from "../../common/api/api";

const PAYMENT_SETTINGS_KEY = "checkout_payment_settings";

const DEFAULT_PAYMENT_SETTINGS = {
  title: "Secure Checkout",
  subtitle: "Fast delivery with secure payment options",
  note: "Online payment methods are captured as selected payment preference in this demo flow. Final gateway processing can be integrated with your payment provider.",
  methods: [
    { value: "cod", label: "Cash on Delivery", hint: "Pay when your order arrives", icon: "🚚", kind: "cod", enabled: true, accountName: "", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "credit_card", label: "Credit Card", hint: "Visa / Amex / Others", icon: "💳", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "debit_card", label: "Debit Card", hint: "Any bank debit card", icon: "🏦", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "master_card", label: "Master Card", hint: "Mastercard secure payment", icon: "🛡", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "easypaisa", label: "Easypaisa", hint: "Pay with Easypaisa wallet", icon: "📱", kind: "wallet", enabled: true, accountName: "TechZone Easypaisa", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "jazzcash", label: "JazzCash", hint: "Pay with JazzCash wallet", icon: "⚡", kind: "wallet", enabled: true, accountName: "TechZone JazzCash", accountNumber: "", accountLink: "", qrCodeUrl: "" },
  ],
};

const cloneDefaults = () => ({
  ...DEFAULT_PAYMENT_SETTINGS,
  methods: DEFAULT_PAYMENT_SETTINGS.methods.map((method) => ({ ...method })),
});

const CartItems = () => {
  const { products, cartItems, removeFromCart, getTotalCartAmount } = useContext(ShopContext);

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount, message }
  const [couponError, setCouponError] = useState("");

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    address: "",
    mobile: "",
    paymentMethod: "cod",
    cardName: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    walletNumber: "",
    saveToProfile: false,
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState("");
  const [paymentSettings, setPaymentSettings] = useState(cloneDefaults());

  useEffect(() => {
    const loadCheckoutProfile = async () => {
      if (!showCheckout || !localStorage.getItem("auth-token")) return;
      setProfileLoading(true);
      try {
        const userData = await apiGetUser();
        setCheckoutForm((prev) => ({
          ...prev,
          address: userData?.address || "",
          mobile: userData?.mobile || "",
        }));
        setShowAddressEditor(!(userData?.address || userData?.mobile));
      } catch {
        setShowAddressEditor(true);
      } finally {
        setProfileLoading(false);
      }
    };

    loadCheckoutProfile();
  }, [showCheckout]);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const data = await getPaymentSettings();
        const raw = data?.[PAYMENT_SETTINGS_KEY];
        if (raw) {
          const parsed = JSON.parse(raw);
          setPaymentSettings({
            ...cloneDefaults(),
            ...parsed,
            methods: Array.isArray(parsed?.methods) ? parsed.methods : cloneDefaults().methods,
          });
        }
      } catch {
        setPaymentSettings(cloneDefaults());
      }
    };

    loadPaymentSettings();
  }, []);

  useEffect(() => {
    const firstEnabled = paymentSettings.methods.find((method) => method.enabled !== false);
    const currentEnabled = paymentSettings.methods.some((method) => method.value === checkoutForm.paymentMethod && method.enabled !== false);
    if (firstEnabled && !currentEnabled) {
      setCheckoutForm((prev) => ({ ...prev, paymentMethod: firstEnabled.value }));
    }
  }, [paymentSettings, checkoutForm.paymentMethod]);

  const subtotal = getTotalCartAmount();
  const discountAmount = appliedCoupon ? appliedCoupon.discount : 0;
  const finalTotal = Math.max(0, subtotal - discountAmount);
  const enabledMethods = paymentSettings.methods.filter((method) => method.enabled !== false);
  const selectedMethod = enabledMethods.find((method) => method.value === checkoutForm.paymentMethod) || enabledMethods[0] || null;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    setAppliedCoupon(null);
    try {
      const data = await apiValidateCoupon({ code: couponInput.trim(), amount: subtotal });
      if (data.success) {
        setAppliedCoupon({ code: data.code, discount: data.discount, message: data.message });
      } else {
        setCouponError(data.error || "Invalid coupon");
      }
    } catch {
      setCouponError("Could not validate coupon. Please try again.");
    }
    setCouponLoading(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const placeOrder = async () => {
    if (!checkoutForm.address.trim()) { alert("Please enter a delivery address"); return; }
    if (!checkoutForm.mobile.trim()) { alert("Please enter mobile number"); return; }
    if (!localStorage.getItem("auth-token")) { alert("Please login to place an order"); return; }

    setPaymentError("");
    const selectedPayment = checkoutForm.paymentMethod;
    const selectedMethod = paymentSettings.methods.find((method) => method.value === selectedPayment && method.enabled !== false);
    if (!selectedMethod) {
      setPaymentError("Please select a valid payment method.");
      return;
    }

    const cardMethods = enabledMethods.filter((method) => method.kind === "card").map((method) => method.value);

    if (cardMethods.includes(selectedPayment)) {
      if (!checkoutForm.cardName.trim() || !checkoutForm.cardNumber.trim() || !checkoutForm.cardExpiry.trim() || !checkoutForm.cardCvv.trim()) {
        setPaymentError("Please complete card details.");
        return;
      }
    }

    const walletMethods = enabledMethods.filter((method) => method.kind === "wallet").map((method) => method.value);
    if (walletMethods.includes(selectedPayment) && !checkoutForm.walletNumber.trim()) {
      setPaymentError("Please enter wallet mobile number.");
      return;
    }

    setPlacing(true);
    const orderProducts = products
      .filter(e => cartItems[e.id] > 0)
      .map(e => ({ productId: e.id, name: e.name, quantity: cartItems[e.id], price: e.new_price, image: e.image }));

    try {
      if (checkoutForm.saveToProfile) {
        await apiUpdateProfile({
          address: checkoutForm.address,
          mobile: checkoutForm.mobile,
        });
      }

      const data = await apiPlaceOrder({
        products: orderProducts,
        totalAmount: finalTotal,
        discount: discountAmount,
        couponCode: appliedCoupon ? appliedCoupon.code : "",
        address: checkoutForm.address,
        mobile: checkoutForm.mobile,
        paymentMethod: checkoutForm.paymentMethod,
      });
      if (data.success) {
        setOrderSuccess(`Order placed! Order ID: ${data.orderId}`);
        setShowCheckout(false);
        setAppliedCoupon(null);
        setCouponInput("");
        window.location.reload(); // refresh cart (now cleared)
      } else {
        alert(data.error || "Failed to place order");
      }
    } catch {
      alert("Could not place order. Please try again.");
    }
    setPlacing(false);
  };

  return (
    <div className="mx-[10%] my-16 max-[1280px]:mx-[5%] max-[1280px]:my-12 max-[500px]:mx-4 max-[500px]:my-8">

      {orderSuccess && (
        <div className="mb-6 px-5 py-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 font-medium text-center">
          ✓ {orderSuccess}
        </div>
      )}

      {/* Table header — hidden on mobile */}
      <div className="hidden sm:grid grid-cols-[0.5fr_2fr_1fr_1fr_1fr_1fr] items-center gap-8 py-4 px-4 rounded-xl bg-white/[0.04] border border-white/[0.08] text-[#a0aec0] text-sm font-semibold uppercase tracking-[1px] mb-3 max-[1280px]:grid-cols-[0.5fr_3fr_0.5fr_0.5fr_0.5fr_0.5fr] max-[1280px]:gap-4">
        <p>Image</p>
        <p>Product</p>
        <p>Price</p>
        <p>Qty</p>
        <p>Total</p>
        <p>Remove</p>
      </div>

      {/* Cart rows */}
      <div className="flex flex-col gap-2">
        {products.map((e) => {
          if (cartItems[e.id] > 0) {
            return (
              <div key={e.id}>
                {/* Desktop row */}
                <div className="hidden sm:grid grid-cols-[0.5fr_2fr_1fr_1fr_1fr_1fr] items-center gap-8 py-4 px-4 bg-white/[0.03] border border-white/[0.07] rounded-xl text-[#c0cce0] text-sm font-medium hover:border-[rgba(0,212,255,0.25)] transition-colors max-[1280px]:grid-cols-[0.5fr_3fr_0.5fr_0.5fr_0.5fr_0.5fr] max-[1280px]:gap-4">
                  <img className="h-[60px] w-[60px] object-cover rounded-xl border border-white/[0.08] max-[1280px]:h-[50px] max-[1280px]:w-[50px]" src={String(e.image).startsWith('http') ? e.image : backend_url + e.image} alt="" />
                  <p className="text-[#e2e8f0] font-semibold truncate">{e.name}</p>
                  <p className="text-[#00d4ff] font-bold">{currency}{e.new_price}</p>
                  <div className="w-[50px] h-[38px] border border-[rgba(0,212,255,0.25)] bg-white/[0.05] rounded-lg text-[#e2e8f0] text-sm font-bold flex items-center justify-center max-[1280px]:w-10 max-[1280px]:h-8">{cartItems[e.id]}</div>
                  <p className="text-[#e2e8f0] font-bold">{currency}{(e.new_price * cartItems[e.id]).toFixed(2)}</p>
                  <button onClick={() => removeFromCart(e.id)} className="w-8 h-8 rounded-full bg-white/[0.04] border border-white/[0.1] flex items-center justify-center cursor-pointer hover:bg-red-500/20 hover:border-red-500/50 transition-colors mx-auto">
                    <img src={cross_icon} alt="" className="w-3 brightness-0 invert opacity-60 hover:opacity-100" />
                  </button>
                </div>
                {/* Mobile card */}
                <div className="sm:hidden flex items-center gap-3 py-3 px-4 bg-white/[0.03] border border-white/[0.07] rounded-xl hover:border-[rgba(0,212,255,0.25)] transition-colors">
                  <img className="h-[56px] w-[56px] object-cover rounded-xl border border-white/[0.08] shrink-0" src={String(e.image).startsWith('http') ? e.image : backend_url + e.image} alt="" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#e2e8f0] text-sm font-semibold truncate">{e.name}</p>
                    <p className="text-[#00d4ff] text-sm font-bold mt-0.5">{currency}{e.new_price}</p>
                    <p className="text-[#a0aec0] text-xs mt-0.5">Qty: {cartItems[e.id]} · Total: <span className="text-white font-semibold">{currency}{(e.new_price * cartItems[e.id]).toFixed(2)}</span></p>
                  </div>
                  <button onClick={() => removeFromCart(e.id)} className="w-8 h-8 rounded-full bg-white/[0.04] border border-white/[0.1] flex items-center justify-center cursor-pointer hover:bg-red-500/20 hover:border-red-500/50 transition-colors shrink-0">
                    <img src={cross_icon} alt="" className="w-3 brightness-0 invert opacity-60 hover:opacity-100" />
                  </button>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Bottom section */}
      <div className="flex mt-16 gap-10 max-[1280px]:mt-12 max-[1280px]:flex-wrap max-[500px]:mt-8 max-[500px]:gap-6">
        {/* Cart totals */}
        <div className="flex-1 min-w-[280px] max-w-[400px] max-[1280px]:max-w-none">
          <h2 className="text-white text-xl font-bold mb-6">Cart Totals</h2>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-white/[0.06]">
              <span className="text-[#a0aec0] text-sm font-medium">Subtotal</span>
              <span className="text-[#e2e8f0] font-semibold">{currency}{subtotal.toFixed(2)}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between items-center px-6 py-4 border-b border-white/[0.06]">
                <span className="text-green-400 text-sm font-medium">Discount ({appliedCoupon.code})</span>
                <span className="text-green-400 font-semibold">- {currency}{discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center px-6 py-4 border-b border-white/[0.06]">
              <span className="text-[#a0aec0] text-sm font-medium">Shipping Fee</span>
              <span className="text-[#22c55e] font-semibold text-sm">Free</span>
            </div>
            <div className="flex justify-between items-center px-6 py-5">
              <span className="text-white font-bold text-base">Total</span>
              <span className="text-[#00d4ff] font-black text-xl">{currency}{finalTotal.toFixed(2)}</span>
            </div>
          </div>
          <button
            onClick={() => setShowCheckout(true)}
            className="mt-5 w-full h-[52px] border-none rounded-xl text-[#060c18] text-sm font-extrabold cursor-pointer tracking-[0.5px] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_36px_rgba(0,212,255,0.45)] font-[inherit]"
            style={{ background: 'linear-gradient(135deg, #00d4ff, #0077aa)', boxShadow: '0 0 24px rgba(0,212,255,0.25)' }}
          >
            PROCEED TO CHECKOUT →
          </button>
        </div>

        {/* Promo code */}
        <div className="flex-1 min-w-[280px]">
          <h2 className="text-white text-xl font-bold mb-6">Promo Code</h2>
          <p className="text-[#6b7a8d] text-sm mb-4">Have a discount code? Enter it below.</p>

          {appliedCoupon ? (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <p className="text-green-400 font-semibold text-sm mb-1">✓ {appliedCoupon.message}</p>
              <button onClick={removeCoupon} className="text-xs text-[#4a5568] hover:text-red-400 underline cursor-pointer">Remove coupon</button>
            </div>
          ) : (
            <>
              <div className="flex items-center h-[52px] bg-white/[0.04] border border-white/[0.1] rounded-xl overflow-hidden focus-within:border-[rgba(0,212,255,0.4)] transition-colors max-w-[460px]">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={couponInput}
                  onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(""); }}
                  onKeyDown={e => e.key === "Enter" && applyCoupon()}
                  className="border-none outline-none bg-transparent text-[#e2e8f0] text-sm flex-1 h-full px-5 placeholder:text-[#4a5568] font-[inherit]"
                />
                <button
                  onClick={applyCoupon}
                  disabled={couponLoading}
                  className="h-full px-7 border-none text-sm font-bold text-[#060c18] cursor-pointer font-[inherit] transition-all hover:brightness-110 disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #00d4ff, #0077aa)' }}
                >
                  {couponLoading ? "..." : "Apply"}
                </button>
              </div>
              {couponError && <p className="mt-2 text-red-400 text-xs">{couponError}</p>}
            </>
          )}
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-[2000] flex items-start sm:items-center justify-center px-3 pt-2 pb-3 sm:p-6 overflow-y-auto" style={{ background: "linear-gradient(180deg, rgba(2,6,23,0.82) 0%, rgba(0,0,0,0.9) 100%)" }}>
          <div className="relative bg-[#0d1527] border border-white/[0.12] rounded-3xl p-4 sm:p-5 w-full max-w-2xl shadow-2xl max-h-[calc(100vh-1rem)] sm:max-h-[88vh] overflow-y-auto overflow-x-hidden my-0 sm:my-auto">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#00d4ff] via-[#22d3ee] to-[#0ea5e9]" />

            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">{paymentSettings.title}</h2>
                <p className="text-xs text-[#7f8ea3] mt-0.5">{paymentSettings.subtitle}</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-[#4a5568] hover:text-white text-2xl leading-none cursor-pointer">×</button>
            </div>

            <div className="mb-4 p-3 bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.08] rounded-2xl text-sm space-y-1">
              <div className="flex justify-between text-[#a0aec0]"><span>Subtotal</span><span>{currency}{subtotal.toFixed(2)}</span></div>
              {appliedCoupon && <div className="flex justify-between text-green-400"><span>Discount ({appliedCoupon.code})</span><span>- {currency}{discountAmount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-white font-bold pt-1 border-t border-white/[0.06]"><span>Total</span><span className="text-[#00d4ff]">{currency}{finalTotal.toFixed(2)}</span></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-4 lg:gap-5">
              <div className="space-y-3">
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3.5">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="text-xs text-[#a0aec0] block">Delivery Details</label>
                    <button onClick={() => setShowAddressEditor((prev) => !prev)} className="text-xs text-[#00d4ff] hover:text-[#5ee5ff] underline">
                      {showAddressEditor ? "Use saved" : "Change address"}
                    </button>
                  </div>

                  {!showAddressEditor ? (
                    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 space-y-1">
                      {profileLoading ? (
                        <p className="text-[#7f8ea3] text-sm">Loading your profile details...</p>
                      ) : (
                        <>
                          <p className="text-white text-sm font-semibold">{checkoutForm.address || "No address in profile"}</p>
                          <p className="text-[#9eb1c9] text-xs">Mobile: {checkoutForm.mobile || "Not set"}</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={checkoutForm.address}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, address: e.target.value })}
                        placeholder="Street, City, Province, Postal Code"
                        rows={3}
                        className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm resize-none"
                      />
                      <input
                        type="text"
                        value={checkoutForm.mobile}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, mobile: e.target.value })}
                        placeholder="Mobile Number"
                        className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm"
                      />
                      <label className="flex items-center gap-2 text-xs text-[#9eb1c9]">
                        <input
                          type="checkbox"
                          checked={checkoutForm.saveToProfile}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, saveToProfile: e.target.checked })}
                        />
                        Save this address and mobile to my profile
                      </label>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3.5">
                  <label className="text-xs text-[#a0aec0] mb-2 block">Payment Method</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {enabledMethods.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => { setCheckoutForm({ ...checkoutForm, paymentMethod: option.value }); setPaymentError(""); }}
                        className={`text-left rounded-xl border px-2.5 py-2 transition-all ${checkoutForm.paymentMethod === option.value ? "border-[#00d4ff]/70 bg-[#00d4ff]/10" : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.2]"}`}
                      >
                        <p className="text-xs text-white font-semibold leading-tight">{option.icon} {option.label}</p>
                        <p className="text-[10px] text-[#7f8ea3] mt-0.5 leading-tight">{option.hint}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {paymentError && <p className="text-red-400 text-xs">{paymentError}</p>}
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-[rgba(0,212,255,0.18)] bg-[rgba(0,212,255,0.05)] p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#00d4ff] font-bold mb-2">Payment Destination</p>
                  <p className="text-white font-bold text-sm">{selectedMethod?.icon} {selectedMethod?.label || "Payment method"}</p>
                  <p className="text-[#9eb1c9] text-xs mt-1">{selectedMethod?.hint}</p>

                  {selectedMethod?.accountName && <p className="text-sm text-white mt-3 font-semibold">{selectedMethod.accountName}</p>}
                  {selectedMethod?.accountNumber && <p className="text-sm text-[#e2e8f0] mt-1 break-all">{selectedMethod.accountNumber}</p>}
                  {selectedMethod?.accountLink && (
                    <a href={selectedMethod.accountLink} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-[#00d4ff] underline break-all">
                      Open payment link
                    </a>
                  )}
                  {selectedMethod?.qrCodeUrl && (
                    <img src={selectedMethod.qrCodeUrl} alt="Payment QR" className="mt-3 w-full max-w-[180px] rounded-xl border border-white/[0.1]" />
                  )}
                </div>

                {selectedMethod?.kind === "card" && (
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3 space-y-2">
                    <input type="text" value={checkoutForm.cardName} onChange={e => setCheckoutForm({ ...checkoutForm, cardName: e.target.value })} placeholder="Card Holder Name" className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm" />
                    <input type="text" value={checkoutForm.cardNumber} onChange={e => setCheckoutForm({ ...checkoutForm, cardNumber: e.target.value })} placeholder="Card Number" className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm" />
                    <div className="grid grid-cols-2 gap-2.5">
                      <input type="text" value={checkoutForm.cardExpiry} onChange={e => setCheckoutForm({ ...checkoutForm, cardExpiry: e.target.value })} placeholder="MM/YY" className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm" />
                      <input type="password" value={checkoutForm.cardCvv} onChange={e => setCheckoutForm({ ...checkoutForm, cardCvv: e.target.value })} placeholder="CVV" className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm" />
                    </div>
                  </div>
                )}

                {selectedMethod?.kind === "wallet" && (
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3 space-y-2">
                    <input type="text" value={checkoutForm.walletNumber} onChange={e => setCheckoutForm({ ...checkoutForm, walletNumber: e.target.value })} placeholder="Wallet Mobile Number" className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm" />
                  </div>
                )}

                <p className="text-[11px] text-[#7f8ea3] leading-relaxed">{paymentSettings.note}</p>

                <div className="flex gap-3 pt-1 sticky bottom-0 bg-gradient-to-t from-[#0d1527] via-[#0d1527] to-transparent">
                  <button
                    onClick={placeOrder}
                    disabled={placing}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00d4ff] to-[#0077aa] text-[#060c18] font-extrabold rounded-xl text-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
                  >
                    {placing ? "Placing Order..." : `Place Order · ${currency}${finalTotal.toFixed(2)}`}
                  </button>
                  <button onClick={() => setShowCheckout(false)} className="px-5 py-3 border border-white/[0.1] text-[#a0aec0] rounded-xl text-sm hover:border-white/[0.25] cursor-pointer">Back</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItems;
